import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GlobalLoader, ProtectedRoute, PublicRoute } from '@/components';
import { DashboardLayout } from '@/components/layout';
import { LoginPage, NotFoundPage } from '@/pages';
import { RegisterPage, VerifyEmailPage, ResetPasswordPage, ForgotPasswordPage, ResendVerificationPage } from '@/pages/auth';
import { DashboardPage, ProfilePage } from '@/pages/dashboard';
import { MyPostsPage } from '@/pages/my-posts/MyPostsPage';
import { ApplicationsPage } from '@/pages/applications/ApplicationsPage';
import { MyApplicationsPage } from '@/pages/my-applications/MyApplicationsPage';
import { JobCategoriesPage } from '@/pages/jobCategories';
import { CompaniesPage } from '@/pages/companies';
import { AssessmentPage } from '@/pages/assessment';
import { AttemptsHistoryPage, AttemptDetailsPage } from '@/pages/attempts';
import CertificateViewPage from '@/pages/certificate/CertificateViewPage';
import TestCertificatePage from '@/pages/certificate/TestCertificatePage';
import { HRContentPage } from '@/pages/hr';
import { ExamComponent } from '@/components/exam/ExamComponent';
import { ProfileSuggestions } from '@/pages/ProfileSuggestions';
import { ProfilePage as UserProfilePage } from '@/pages/Profile';
import { NetworkPage } from '@/pages/dashboard/NetworkPage';
import { UsersPage, FoldersPage, KnowledgeBasePage, ReportsPage, EventReportsPage, ServiceProductReportsPage, BusinessCourseReportsPage } from '@/pages/admin';
import { EventsPage } from '@/pages/events/EventsPage';
import { EventDetailPage } from '@/pages/events/EventDetailPage';
import { EnrolledUsersPage } from '@/pages/events/EnrolledUsersPage';
import { MyEventsPage } from '@/pages/events/MyEventsPage';
import { NewsPage } from '@/pages/news/NewsPage';
import { NewsDetailPage } from '@/pages/news/NewsDetailPage';
import { DocumentExplorerPage } from '@/pages/documents/DocumentExplorerPage';
import { NotificationPage } from '@/pages/dashboard/NotificationPage';
import { MyBusinessPage, AllBusinessesPage, ManageBusinessPage, BusinessDetailPage, BusinessDetailsPage, ServiceProductDetailPage, ExploreProductsServicesPage, BusinessCourseDetailPage, ExploreCoursesPage } from '@/pages/business';
import { AddEditBusinessPage } from '@/pages/business/AddEditBusinessPage';
import { USER_ROLES } from '@/constants/app';

export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<GlobalLoader />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        <Route
          path="/verify-email"
          element={
            <PublicRoute>
              <VerifyEmailPage />
            </PublicRoute>
          }
        />

        <Route
          path="/reset"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />

        <Route
          path="/resend-verification"
          element={
            <PublicRoute>
              <ResendVerificationPage />
            </PublicRoute>
          }
        />
        
        {/* Home - All authenticated users */}
        <Route
          path="/talky"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
        </Route>

        {/* Profile - All authenticated users */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProfilePage />} />
        </Route>

        {/* Profile Suggestions - All authenticated users */}
        <Route
          path="/profile-suggestions"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProfileSuggestions />} />
        </Route>

        {/* Network - All authenticated users */}
        <Route
          path="/network"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<NetworkPage />} />
        </Route>

        {/* Notifications - All authenticated users */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<NotificationPage />} />
        </Route>

        {/* User Profile View - All authenticated users */}
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<UserProfilePage />} />
        </Route>

        {/* My Posts - All authenticated users */}
        <Route
          path="/my-talky"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyPostsPage />} />
        </Route>

        {/* Applications - All authenticated users */}
        <Route
          path="/applications/:feedId"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ApplicationsPage />} />
        </Route>

        {/* My Applications - All authenticated users */}
        <Route
          path="/my-applications"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyApplicationsPage />} />
        </Route>

        {/* Settings - Admin only */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Settings - Admin Only</div>} />
        </Route>

        {/* Job Categories - Admin only */}
        <Route
          path="/job-categories"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<JobCategoriesPage />} />
        </Route>

        {/* Companies - All users */}
        <Route
          path="/companies"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<CompaniesPage />} />
        </Route>

        {/* Business Routes */}

        {/* My Business - All authenticated users */}
        <Route
          path="/my-business"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyBusinessPage />} />
          <Route path="new" element={<AddEditBusinessPage />} />
          <Route path="edit/:id" element={<AddEditBusinessPage />} />
          <Route path=":businessId" element={<BusinessDetailPage />} />
        </Route>

        {/* All Businesses - All authenticated users */}
        <Route
          path="/businesses"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AllBusinessesPage />} />
          <Route path=":businessId" element={<BusinessDetailPage />} />
          <Route path=":businessId/serviceProducts/:serviceProductId" element={<ServiceProductDetailPage />} />
          <Route path=":businessId/courses/:courseId" element={<BusinessCourseDetailPage />} />
        </Route>

        {/* Explore Products & Services - All authenticated users */}
        <Route
          path="/products-services"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ExploreProductsServicesPage />} />
          <Route path=":serviceProductId" element={<ServiceProductDetailPage />} />
        </Route>

        {/* Explore Courses - All authenticated users */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ExploreCoursesPage />} />
          <Route path=":courseId" element={<BusinessCourseDetailPage />} />
        </Route>

        {/* Manage Business - Super Admin only */}
        <Route
          path="/admin/manage-business"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ManageBusinessPage />} />
          <Route path=":id" element={<BusinessDetailsPage />} />
        </Route>

        {/* Students - Admin/Staff */}
        <Route
          path="/students"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Students Management - Admin/Staff</div>} />
        </Route>

        {/* Teachers - Admin only */}
        <Route
          path="/teachers"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Teachers Management - Admin Only</div>} />
        </Route>

        {/* Classes - Admin/Staff */}
        <Route
          path="/classes"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Classes Management - Admin/Staff</div>} />
        </Route>

        {/* Reports - Admin only */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ReportsPage />} />
        </Route>

        {/* Event Reports - Admin only */}
        <Route
          path="/admin/event-reports"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<EventReportsPage />} />
        </Route>

        {/* Service/Product Reports - Admin only */}
        <Route
          path="/admin/service-product-reports"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ServiceProductReportsPage />} />
        </Route>

        {/* Business Course Reports - Admin only */}
        <Route
          path="/admin/course-reports"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BusinessCourseReportsPage />} />
        </Route>

        {/* Admin Users - Admin only */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<UsersPage />} />
        </Route>

        {/* Admin Folders - Admin only */}
        <Route
          path="/admin/folders"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<FoldersPage />} />
        </Route>

        {/* Events - All authenticated users */}
        <Route
          path="/events"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<EventsPage />} />
          <Route path=":eventId" element={<EventDetailPage />} />
          <Route path=":eventId/enrolled-users" element={<EnrolledUsersPage />} />
        </Route>

        {/* My Events - All authenticated users */}
        <Route
          path="/my-events"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MyEventsPage />} />
        </Route>

        {/* Admin Knowledge Base - Admin only */}
        <Route
          path="/admin/documents-vault"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<KnowledgeBasePage />} />
        </Route>

        {/* Attendance - Admin/Staff */}
        <Route
          path="/attendance"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Attendance - Admin/Staff</div>} />
        </Route>

        {/* Assignments - Admin/Staff/Student */}
        <Route
          path="/assignments"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.USER, USER_ROLES.STUDENT]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<div>Assignments - Admin/Staff/Student</div>} />
        </Route>

        {/* HR Content - All authenticated users */}
        <Route
          path="/hr"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HRContentPage />} />
        </Route>

        {/* Document Explorer - All authenticated users */}
        <Route
          path="/documents"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DocumentExplorerPage />} />
        </Route>

        {/* News - All authenticated users */}
        <Route
          path="/news"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<NewsPage />} />
          <Route path=":newsId" element={<NewsDetailPage />} />
        </Route>

        {/* Assessment - All authenticated users */}
        <Route
          path="/assessment"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AssessmentPage />} />
          
          {/* Assessment Management - Admin/Super Admin only */}
          <Route 
            path="manage" 
            element={
              <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
                <div>Assessment Management - Admin Only</div>
              </ProtectedRoute>
            } 
          />
          
          {/* Question Management - Admin/Super Admin only */}
          <Route 
            path="questions" 
            element={
              <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR]}>
                <div>Question Management - Admin Only</div>
              </ProtectedRoute>
            } 
          />
          
          {/* Analytics - Admin/Super Admin + Users can see own analytics */}
          <Route 
            path="analytics" 
            element={
              <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.STUDENT, USER_ROLES.PARENTS]}>
                <div>Assessment Analytics</div>
              </ProtectedRoute>
            } 
          />
          
          {/* Take Assessment - All users */}
          <Route 
            path="take/:id" 
            element={
              <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.STUDENT, USER_ROLES.PARENTS]}>
                <div>Take Assessment</div>
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* Attempts - All authenticated users */}
        <Route
          path="/attempts"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AttemptsHistoryPage />} />
          <Route path=":attemptId" element={<AttemptDetailsPage />} />
        </Route>

        {/* Exam Route - All authenticated users (outside DashboardLayout) */}
        <Route
          path="/exam/:id"
          element={
            <ProtectedRoute requiredRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMINISTRATOR, USER_ROLES.STAFF, USER_ROLES.USER, USER_ROLES.PARENTS]}>
              <ExamComponent />
            </ProtectedRoute>
          }
        />

        {/* Certificate Test Route - Public, no authentication required */}
        <Route
          path="/certificate/test/:attemptId"
          element={<TestCertificatePage />}
        />

        {/* Certificate View Route - Public, no authentication required */}
        <Route
          path="/certificate/:attemptId"
          element={<CertificateViewPage />}
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={<Navigate to="/assessment" replace />}
        />
        
        {/* 404 Page */}
        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
    </Suspense>
  );
};